import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import { CreditNoteProposalStatus, ProductOrderDto, ProductOrderPaymentStatus, ProductOrderStatus } from '@services/dtos'
import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Button, Container, Dialog, DialogContent, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import { UpTransition } from '../animations'
import { getProductOrders, markOrderAsReceived, markOrderAsPaid, retryFailedMailSending, setProductOrderFilters } from '@state/reducers/productOrderReducer'
import { useSelector } from '@state/store'
import { ProductOrderCancellationReasonLabels, ProductOrderStatusLabels } from '@utils/constants'
import TaskIcon from '@mui/icons-material/Task'
import ProductOrderAcknowledgmentForm from '../forms/productOrder/ProductOrderAcknowledgmentForm'
import ProductOrderPaymentForm from '../forms/productOrder/ProductOrderPaymentForm'
import PaymentIcon from '@mui/icons-material/Payment'
import BlockIcon from '@mui/icons-material/Block'
import InfoIcon from '@mui/icons-material/Info'
import ProductOrderCancellationForm from '../forms/productOrder/ProductOrderCancellationForm'
import ArchiveIcon from '@mui/icons-material/Archive'
import ProductOrderReceiptForm from '../forms/productOrderReceipt/ProductOrderReceiptForm'
import ProductOrderReceiptsTable from './ProductOrderReceiptsTable'
import { debounce, truncate } from 'lodash'
import ProductOrdersTableToolbar from './Toolbars/ProductOrdersTableToolbar'
import ConfirmDialog from '../dialogs/ConfirmDialog'
import { Replay } from '@mui/icons-material'
import { productOrdersColumns } from '@utils/gridColumns'
import ProductOrderDiscrepanciesInfo from '../ProductOrderDiscrepanciesInfo'
import ProductOrderSendingCreditNote from '../ProductOrderSendingCreditNote'
import { useProductOrderSSE } from '@hooks/useProductOrderSSE'

export default function ProductOrdersTable() {
  useProductOrderSSE()
  const [isOrderAcknowledgmentFormOpen, setIsOrderAcknowledgmentFormOpen] = useState(false)
  const [confirmReceivedOpen, setConfirmReceivedOpen] = useState(false)
  const [isOrderReceiptFormOpen, setIsOrderReceiptFormOpen] = useState(false)
  const [isOrderReceiptsTableOpen, setIsOrderReceiptsTableOpen] = useState(false)
  const [isOrderCancellationFormOpen, setIsOrderCancellationFormOpen] = useState(false)
  const [cancellationInfoOpen, setCancellationInfoOpen] = useState(false)
  const [rejectionInfoOpen, setRejectionInfoOpen] = useState(false)
  const [discrepanciesInfoOpen, setDiscrepanciesInfoOpen] = useState(false)
  const [acknowledgmentInfoOpen, setAcknowledgmentInfoOpen] = useState(false)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [receiptMenuAnchorEl, setReceiptMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [paymentMenuAnchorEl, setPaymentMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [dismissedCreditNoteOrderIds, setDismissedCreditNoteOrderIds] = useState<Set<string>>(new Set())
  const [acknowledgmentMenuAnchorEl, setAcknowledgmentMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false)
  const [isPaymentsTableOpen, setIsPaymentsTableOpen] = useState(false)
  const [confirmMarkAsPaidOpen, setConfirmMarkAsPaidOpen] = useState(false)
  const { productOrders, filters, totalCount, loading } = useSelector(({ productOrder }) => ({
    productOrders: productOrder.crud.items.datas,
    filters: productOrder.crud.filters,
    totalCount: productOrder.crud.items.totalCount,
    loading: productOrder.crud.loading,
  }))

  const selectedItem = useMemo(
    () => productOrders.find(o => o.id === selectedItemId) ?? null,
    [productOrders, selectedItemId]
  )

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(getProductOrders())
    return () => {
      dispatch(setProductOrderFilters({ statuses: undefined, paymentStatus: undefined, from: undefined, to: undefined,
        internalOrderNumber: undefined, page: 0 }))
    }
  }, [])

  useEffect(() => {
    const debouncedSearch = debounce(() => {
      dispatch(getProductOrders())
    }, 300)

    debouncedSearch()

    return () => {
      debouncedSearch.cancel()
    }
  }, [filters, dispatch])
  
  const columns: GridColumns<ProductOrderDto> = useMemo(
    () => [
      {
        field: '__actions__',
        headerName: 'Actions',
        width: 130,
        sortable: false,
        renderCell: (params: any) => {
          const row = params.row as ProductOrderDto
          const actions = [
             ...([
              ProductOrderStatus.CANCELLED, 
              ProductOrderStatus.PENDING_CANCELLATION_MAIL, 
              ProductOrderStatus.CANCELLATION_MAIL_FAILED,
            ].includes(row.status) ? [{
              key: 'cancellationInfo',
              label: 'Détails annulation',
              icon: <InfoIcon />,
              onClick: () => {
                setSelectedItemId(row.id)
                setCancellationInfoOpen(true)
              },
            }] : []),
            ...(row.status === ProductOrderStatus.REJECTED ? [{
              key: 'rejectionInfo',
              label: 'Détails rejet',
              icon: <InfoIcon />,
              onClick: () => {
                setSelectedItemId(row.id)
                setRejectionInfoOpen(true)
              },
            }] : []),
            ...([
              ProductOrderStatus.PENDING_ORDER_ACKNOWLEDGMENT, 
              ProductOrderStatus.PENDING_DELIVERY, 
              ProductOrderStatus.PARTIALLY_RECEIVED,
              ProductOrderStatus.RECEIVED,
              ProductOrderStatus.RECEIVED_WITH_DISCREPANCIES
            ].includes(row.status) ? [{
              key: 'orderAcknowledgment',
              label: row.status === ProductOrderStatus.RECEIVED || row.status === ProductOrderStatus.RECEIVED_WITH_DISCREPANCIES 
                ? 'Historique ARC' 
                : 'ARC',
              icon: <TaskIcon />,
              onClick: (event?: React.MouseEvent<HTMLElement>) => {
                setSelectedItemId(row.id)
                if (row.status === ProductOrderStatus.RECEIVED || row.status === ProductOrderStatus.RECEIVED_WITH_DISCREPANCIES) {
                  setAcknowledgmentInfoOpen(true)
                  return
                }
                if (row.status === ProductOrderStatus.PENDING_ORDER_ACKNOWLEDGMENT) {
                  setIsOrderAcknowledgmentFormOpen(true)
                  return
                }
                setAcknowledgmentMenuAnchorEl(event?.currentTarget ?? null)
              },
            }] : []),
            ...([
              ProductOrderStatus.MAIL_FAILED, 
              ProductOrderStatus.CANCELLATION_MAIL_FAILED, 
              ProductOrderStatus.CREDIT_NOTE_MAIL_FAILED,
            ].includes(row.status) ? [{
              key: 'orderResend',
              label: 'Retenter envoi mail',
              icon: <Replay />,
              disabled: selectedItem?.id === row.id,
              onClick: () => {
                setSelectedItemId(row.id)
                dispatch(retryFailedMailSending(row.id))
              },
            }] : []),
            ...([ProductOrderStatus.PENDING_VALIDATION, ProductOrderStatus.PENDING_ORDER_ACKNOWLEDGMENT].includes(row.status) ? [{
              key: 'orderCancellation',
              label: 'Annuler commande',
              icon: <BlockIcon />,
              onClick: () => {
                setSelectedItemId(row.id)
                setIsOrderCancellationFormOpen(true)
              },
            }] : []),
            ...([ProductOrderStatus.PENDING_DELIVERY,
              ProductOrderStatus.PARTIALLY_RECEIVED,
              ProductOrderStatus.RECEIVED,
              ProductOrderStatus.RECEIVED_WITH_DISCREPANCIES
            ].includes(row.status) ? [{
              key: 'orderReceipt',
              label: row.status === ProductOrderStatus.RECEIVED || row.status === ProductOrderStatus.RECEIVED_WITH_DISCREPANCIES
                ? 'Historique réceptions'
                : 'Réception commande',
              icon: <ArchiveIcon />,
              onClick: (event?: React.MouseEvent<HTMLElement>) => {
                setSelectedItemId(row.id)
                if (row.status === ProductOrderStatus.RECEIVED) {
                  setIsOrderReceiptsTableOpen(true)
                  return
                }
                setReceiptMenuAnchorEl(event?.currentTarget ?? null)
              },
            }] : []),
            ...([
              ProductOrderStatus.PENDING_DELIVERY,
              ProductOrderStatus.PARTIALLY_RECEIVED,
              ProductOrderStatus.RECEIVED,
              ProductOrderStatus.RECEIVED_WITH_DISCREPANCIES,
            ].includes(row.status) && row.paymentStatus === ProductOrderPaymentStatus.PENDING_PAYMENT ? [{
              key: 'payment',
              label: 'Règlement',
              icon: <PaymentIcon />,
              onClick: (event?: React.MouseEvent<HTMLElement>) => {
                setSelectedItemId(row.id)
                if ((row.payments?.length ?? 0) === 0) {
                  setIsPaymentFormOpen(true)
                  return
                }
                setPaymentMenuAnchorEl(event?.currentTarget ?? null)
              },
            }] : []),
            ...((row.payments?.length ?? 0) > 0 && row.paymentStatus === ProductOrderPaymentStatus.PAID ? [{
              key: 'paymentsHistory',
              label: 'Historique paiements',
              icon: <PaymentIcon />,
              onClick: () => {
                setSelectedItemId(row.id)
                setIsPaymentsTableOpen(true)
              },
            }] : []),
          ]
          return (
            <div className="flex space-x-2">
              {actions.map((a) => (
                <Tooltip key={a.key} title={a.label}>
                  <div
                    className={'cursor-pointer p-1 hover:bg-gray-200 rounded'}
                    onClick={(e) => {
                      e.stopPropagation()
                      a.onClick(e)
                    }}
                  >
                    {a.icon}
                  </div>
                </Tooltip>
              ))}
            </div>
          )
        },
      },
      ...productOrdersColumns
    ],
    [],
  )

  return (
    <Container maxWidth={false} style={{ height: '100%', padding: '0px 30px' }}>
      <DataGridPro
        density="standard"
        pagination
        pageSize={20}
        rowsPerPageOptions={[20]}
        paginationMode="server"
        style={{
          height: 'calc(100vh - 198px)',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '15px 15px 25px 25px',
        }}
        components={{ Toolbar: ProductOrdersTableToolbar }}
        onPageChange={(page) => {
          dispatch(setProductOrderFilters({ page }))
        }}
        sx={{
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f8f8f8',
          },
        }}
        columns={columns}
        rows={productOrders}
        rowCount={totalCount}
        loading={loading}
        disableSelectionOnClick
        disableMultipleSelection
      />

      {selectedItem && <>
        <Dialog
          open={isOrderAcknowledgmentFormOpen}
          onClose={() => setIsOrderAcknowledgmentFormOpen(false)}
          TransitionComponent={UpTransition}
          slotProps={{ backdrop: {} }}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent>
            {isOrderAcknowledgmentFormOpen && (
              <ProductOrderAcknowledgmentForm
                productOrder={selectedItem}
                onClose={() => setIsOrderAcknowledgmentFormOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={isOrderCancellationFormOpen}
          onClose={() => setIsOrderCancellationFormOpen(false)}
          TransitionComponent={UpTransition}
          slotProps={{ backdrop: {} }}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent>
            {isOrderCancellationFormOpen && (
              <ProductOrderCancellationForm
                productOrder={selectedItem}
                onClose={() => setIsOrderCancellationFormOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={isOrderReceiptFormOpen}
          onClose={() => setIsOrderReceiptFormOpen(false)}
          TransitionComponent={UpTransition}
          slotProps={{ backdrop: {} }}
          maxWidth="xl"
          fullWidth
          sx={{ 
            '& .MuiDialog-paper': { 
              marginBottom: '80px'
            } 
          }}
        >
          <DialogContent>
            {isOrderReceiptFormOpen && (
              <ProductOrderReceiptForm
                productOrder={selectedItem}
                onClose={() => setIsOrderReceiptFormOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={cancellationInfoOpen}
          onClose={() => setCancellationInfoOpen(false)}
          TransitionComponent={UpTransition}
          maxWidth="md"
          fullWidth
        >
          <DialogContent className="p-0">
            {cancellationInfoOpen && (
              <div className="flex flex-col gap-6 p-6">
                <div className="border-b pb-4">
                  <Typography variant="h6" className="font-semibold">
                    {'Informations d\'annulation'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" className="mt-1">
                    Commande {selectedItem.internalOrderNumber} - {selectedItem.supplier.name} -{' '}
                    {new Date(selectedItem.createdAt).toLocaleDateString('fr-FR')}
                  </Typography>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary">
                      Action
                    </Typography>
                    <Typography variant="body2">
                      {selectedItem.cancelledAt
                        ? new Date(selectedItem.cancelledAt).toLocaleDateString('fr-FR')
                        : 'N/A'} par {selectedItem.cancelledBy?.firstName || ''} {selectedItem.cancelledBy?.lastName || ''}
                    </Typography>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary">
                      Raison
                    </Typography>
                    <Typography variant="body2">
                      {selectedItem.cancellationReason
                        ? ProductOrderCancellationReasonLabels[selectedItem.cancellationReason]
                        : 'N/A'}
                    </Typography>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    className="mb-2 block"
                  >
                    Email destinataire
                  </Typography>
                  <Typography variant="body2">
                    {selectedItem.cancellationEmail || 'N/A (annulée avant que la commande soit envoyée au fournisseur)'}
                  </Typography>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    className="mb-2 block"
                  >
                    Commentaire {selectedItem.cancellationEmail ? 'au fournisseur' : 'interne'}
                  </Typography>
                  <Typography variant="body2">
                    {selectedItem.cancellationComment || 'N/A'}
                  </Typography>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={acknowledgmentInfoOpen}
          onClose={() => setAcknowledgmentInfoOpen(false)}
          TransitionComponent={UpTransition}
          maxWidth="md"
          fullWidth
        >
          <DialogContent className="p-0">
            {acknowledgmentInfoOpen && selectedItem?.acknowledgmentAttachments && (
              <div className="flex flex-col gap-6 p-6">
                <div className="border-b pb-4">
                  <Typography variant="h6" className="font-semibold">
                    {'Historique ARC'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" className="mt-1">
                    Commande {selectedItem.internalOrderNumber} - {selectedItem.supplier.name} -{' '}
                    {new Date(selectedItem.createdAt).toLocaleDateString('fr-FR')}
                  </Typography>
                </div>
                <div className="flex flex-col gap-4">
                  {selectedItem.acknowledgmentAttachments.map((attachment, index) => (
                    <div
                      key={`acknowledgmentAttachments-${index}`}
                      className="bg-gray-50 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex flex-col gap-1">
                        <Typography variant="body2" className="font-medium">
                          {attachment.filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enregistré le {new Date(attachment.createdAt).toLocaleDateString('fr-FR')}
                        </Typography>
                      </div>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => window.open(attachment.url, '_blank')}>
                        Consulter
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={isOrderReceiptsTableOpen}
          onClose={() => setIsOrderReceiptsTableOpen(false)}
          TransitionComponent={UpTransition}
          slotProps={{ backdrop: {} }}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent>
            {isOrderReceiptsTableOpen && (
              <>
                <div className="flex justify-between items-start mb-2">
                  <div className="mb-2">
                    <h2 className="text-2xl font-bold text-gray-800 mr-4">Historique réceptions produits</h2>
                    <Typography variant="body2" color="text.secondary">Commande {selectedItem.internalOrderNumber}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {truncate(selectedItem.supplier.name, { length: 25, omission: '…' })} 
                        - {new Date(selectedItem.createdAt).toLocaleDateString('fr-FR')}
                    </Typography>
                  </div>

                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => setIsOrderReceiptsTableOpen(false)}>
                    Fermer
                  </Button>
                </div>
                <ProductOrderReceiptsTable orderId={selectedItem.id} />
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={discrepanciesInfoOpen}
          onClose={() => setDiscrepanciesInfoOpen(false)}
          TransitionComponent={UpTransition}
          slotProps={{ backdrop: {} }}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent>
            {discrepanciesInfoOpen && (
              <>
                <div className="flex justify-between items-start mb-2">
                  <div className="mb-2">
                    <h2 className="text-2xl font-bold text-gray-800 mr-4">Deltas</h2>
                    <Typography variant="body2" color="text.secondary">Commande {selectedItem.internalOrderNumber}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {truncate(selectedItem.supplier.name, { length: 25, omission: '…' })} 
                      {' '}- {new Date(selectedItem.createdAt).toLocaleDateString('fr-FR')}
                    </Typography>
                  </div>

                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => setDiscrepanciesInfoOpen(false)}>
                    Fermer
                  </Button>
                </div>
                <ProductOrderDiscrepanciesInfo order={selectedItem} />
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={rejectionInfoOpen}
          onClose={() => setRejectionInfoOpen(false)}
          TransitionComponent={UpTransition}
          maxWidth="md"
          fullWidth
        >
          <DialogContent className="p-0">
            {rejectionInfoOpen && (
              <div className="flex flex-col gap-6 p-6">
                <div className="border-b pb-4">
                  <Typography variant="h6" className="font-semibold">
                    {'Informations de rejet'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" className="mt-1">
                    Commande {selectedItem.internalOrderNumber} - {selectedItem.supplier.name} -{' '}
                    {new Date(selectedItem.createdAt).toLocaleDateString('fr-FR')}
                  </Typography>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary">
                      Date
                    </Typography>
                    <Typography variant="body2">
                      {selectedItem.rejectedAt && new Date(selectedItem.rejectedAt).toLocaleDateString('fr-FR')}
                    </Typography>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Typography variant="caption" color="text.secondary">
                      Membre
                    </Typography>
                    <Typography variant="body2">
                      {selectedItem.rejectedBy && `${selectedItem.rejectedBy.firstName} ${selectedItem.rejectedBy.lastName}`}
                    </Typography>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={isPaymentFormOpen}
          onClose={() => setIsPaymentFormOpen(false)}
          TransitionComponent={UpTransition}
          slotProps={{ backdrop: {} }}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent>
            {isPaymentFormOpen && (
              <ProductOrderPaymentForm
                productOrder={selectedItem}
                onClose={() => setIsPaymentFormOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={isPaymentsTableOpen}
          onClose={() => setIsPaymentsTableOpen(false)}
          TransitionComponent={UpTransition}
          maxWidth="md"
          fullWidth
        >
          <DialogContent className="p-0">
            {isPaymentsTableOpen && (selectedItem?.payments?.length ?? 0) > 0 && (
              <div className="flex flex-col gap-6 p-6">
                <div className="border-b pb-4">
                  <Typography variant="h6" className="font-semibold">
                    Historique paiements
                  </Typography>
                  <Typography variant="body2" color="text.secondary" className="mt-1">
                    Commande {selectedItem.internalOrderNumber} - {selectedItem.supplier.name} -{' '}
                    {new Date(selectedItem.createdAt).toLocaleDateString('fr-FR')}
                  </Typography>
                </div>
                <div className="flex flex-col gap-4">
                  {(selectedItem.payments ?? []).map((payment, index) => (
                    <div
                      key={`payment-${index}`}
                      className="bg-gray-50 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                    >
                      <div className="flex flex-col gap-1">
                        <Typography variant="body2" className="font-medium">
                          Paiement du {new Date(payment.paymentDate).toLocaleDateString('fr-FR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enregistré le {new Date(payment.createdAt).toLocaleDateString('fr-FR')}
                        </Typography>
                      </div>
                      {payment.attachment?.url && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => window.open(payment.attachment?.url, '_blank')}
                        >
                          Consulter
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>}

      {selectedItem && <ConfirmDialog
        open={confirmMarkAsPaidOpen}
        title="Marquer comme réglée"
        message={`Êtes-vous sûr de marquer la commande ${selectedItem.internalOrderNumber} comme réglée ?`}
        onCancel={() => setConfirmMarkAsPaidOpen(false)}
        onConfirm={() => {
          setConfirmMarkAsPaidOpen(false)
          dispatch(markOrderAsPaid(selectedItem.id))
        }}
      />}

      <Menu
        anchorEl={receiptMenuAnchorEl}
        open={!!receiptMenuAnchorEl}
        onClose={() => setReceiptMenuAnchorEl(null)}
      >
        {selectedItem && selectedItem.status !== ProductOrderStatus.PENDING_DELIVERY && <MenuItem
          onClick={() => {
            setReceiptMenuAnchorEl(null)
            setIsOrderReceiptsTableOpen(true)
          }}
        >
          Historique
        </MenuItem>}

        {selectedItem && selectedItem.status === ProductOrderStatus.RECEIVED_WITH_DISCREPANCIES ? (
          <MenuItem
              onClick={() => {
                setReceiptMenuAnchorEl(null)
                setDiscrepanciesInfoOpen(true)
              }}
            >
            Consulter les deltas
          </MenuItem>) : (
          <>
            <MenuItem
              onClick={() => {
                setReceiptMenuAnchorEl(null)
                setIsOrderReceiptFormOpen(true)
              }}
            >
            Nouvelle réception
            </MenuItem>

            <MenuItem
              onClick={() => {
                setReceiptMenuAnchorEl(null)
                setConfirmReceivedOpen(true)
              }}
            >
              Marquer comme {ProductOrderStatusLabels[ProductOrderStatus.RECEIVED]}
            </MenuItem>
          </>)}
      </Menu>

      <Menu
        anchorEl={paymentMenuAnchorEl}
        open={!!paymentMenuAnchorEl}
        onClose={() => setPaymentMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setPaymentMenuAnchorEl(null)
            setIsPaymentsTableOpen(true)
          }}
        >
          Historique
        </MenuItem>

        <MenuItem
          onClick={() => {
            setPaymentMenuAnchorEl(null)
            setIsPaymentFormOpen(true)
          }}
        >
          Enregistrer paiement
        </MenuItem>

        <MenuItem
          onClick={() => {
            setPaymentMenuAnchorEl(null)
            setConfirmMarkAsPaidOpen(true)
          }}
        >
          Marquer comme réglée
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={acknowledgmentMenuAnchorEl}
        open={!!acknowledgmentMenuAnchorEl}
        onClose={() => setAcknowledgmentMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAcknowledgmentMenuAnchorEl(null)
            setAcknowledgmentInfoOpen(true)
          }}
        >
          Historique
        </MenuItem>

        <MenuItem
          onClick={() => {
            setAcknowledgmentMenuAnchorEl(null)
            setIsOrderAcknowledgmentFormOpen(true)
          }}
        >
          Nouvel ARC
        </MenuItem>
      </Menu>

      {selectedItem && <ConfirmDialog
        open={confirmReceivedOpen}
        title="Changement statut commande"
        message={`Êtes-vous sûr de passer la commande ${selectedItem.internalOrderNumber} du 
          ${new Date(selectedItem.createdAt).toLocaleDateString('fr-FR')} à ${selectedItem.supplier.name} 
          au statut ${ProductOrderStatusLabels[ProductOrderStatus.RECEIVED]} ? Vous ne pourrez plus enregistrer de 
          nouvelles réceptions de produits.`}
        onCancel={() => setConfirmReceivedOpen(false)}
        onConfirm={() => {
          setConfirmReceivedOpen(false)
          dispatch(markOrderAsReceived(selectedItem.id))
        }}
      />}

      {productOrders
        .filter(({ id, creditNoteProposalStatus }) =>
          creditNoteProposalStatus === CreditNoteProposalStatus.PENDING &&
          !dismissedCreditNoteOrderIds.has(id)
        )
        .map((order) => (
        <Dialog
          key={`${order.id}-credit-note`}
          open={true}
          onClose={() => setDismissedCreditNoteOrderIds(prev => new Set(prev).add(order.id))}
          TransitionComponent={UpTransition}
          slotProps={{ backdrop: {} }}
          maxWidth="md"
          fullWidth
        >
          <DialogContent className="p-0">
            <div className="flex flex-col gap-6 p-6">
              <div className="border-b pb-4">
                <Typography variant="h6" className="font-semibold">
                  Deltas commande
                </Typography>
                <Typography variant="body2" color="text.secondary" className="mt-1">
                  Commande {order.internalOrderNumber} - {order.supplier.name} -{' '}
                  {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                </Typography>
              </div>
              <ProductOrderSendingCreditNote 
                order={order} 
                onDismiss={() => setDismissedCreditNoteOrderIds(prev => new Set(prev).add(order.id))} />
            </div>
          </DialogContent>
        </Dialog>
      ))}
    </Container>
  )
}
